import React from 'react'
import {
  useUsers,
  useStateWithDebounce,
  AllUsers_users_users,
  useDebounce,
  UsersFilterInput,
  useAuth,
  CompanyMemberRole,
  GroupMemberRole,
  UserTypeForFilter,
} from '@modmd/data'
import Datalist from 'components/Datalist'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import Icon from 'components/Icon'

interface Props {
  onAddMember: (user: Omit<AllUsers_users_users, '__typename'>) => void
  filter?: UsersFilterInput
}

const StaffMembersSelector: React.VFC<Props> = ({ onAddMember, filter }) => {
  const { getUserCompaniesWithRole, getUserGroupsWithRole } = useAuth()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const { users, getUsers } = useUsers()
  const [emailOrNameLike, setEmailOrNameLike, debouncedEmailOrNameLike] = useStateWithDebounce('')
  const { companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Owner, CompanyMemberRole.Manager],
  })
  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
  })

  const filterData = React.useMemo(
    () => ({
      ...filter,
      emailOrNameLike: debouncedEmailOrNameLike,
      type: UserTypeForFilter.Staff,
      isDeleted: false,
    }),
    [filter, debouncedEmailOrNameLike]
  )

  useDebounce(
    () => {
      getUsers({
        variables: {
          filterData,
          pagination: {
            from: 0,
            length: 10,
          },
          verification: {
            companyIds: companies?.map((company) => company.id),
            groupIds: groups?.map((groups) => groups.id),
          },
        },
      })
    },
    500,
    [getUsers, debouncedEmailOrNameLike, filterData]
  )

  return (
    <>
      <Datalist
        header={
          <Datalist.Title
            rightContent={
              <>
                <TextInput
                  hasBottomContent={false}
                  isFullWidth
                  leftIcon={<Icon.Search size="L" />}
                  hasTopContent={false}
                  placeholder="Search member"
                  value={emailOrNameLike}
                  onChange={(value) => setEmailOrNameLike(value.target.value)}
                />
              </>
            }
          />
        }
        isLoading={users.loading}
        data={users.data?.users.users || []}
        shouldRenderTableHeader={isDesktop}
        showActionIndicators
        renderTableHeader={() => (
          <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
            <Datalist.HeaderCell label="Email" />
            <Datalist.HeaderCell label="First name" />
            <Datalist.HeaderCell label="Last name" />
            <Datalist.HeaderCell label="Action" />
          </Datalist.Row>
        )}
        renderTableRow={(user) => (
          <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
            <Datalist.Cell label="Email" useEllipsis>
              {user.email}
            </Datalist.Cell>
            <Datalist.Cell label="First name" useEllipsis>
              {user.firstName}
            </Datalist.Cell>
            <Datalist.Cell label="Last name" useEllipsis>
              {user.lastName}
            </Datalist.Cell>
            <Datalist.Cell label="Action">
              <Button
                size="small"
                onClick={() => {
                  onAddMember(user)
                }}
              >
                Add
              </Button>
            </Datalist.Cell>
          </Datalist.Row>
        )}
      />
    </>
  )
}

export { StaffMembersSelector }
